import { Button, Typography } from 'components';
import { ArrowSelectIcon, DoubleArrowSelectIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { FC } from 'react';
import { connect } from 'react-redux';
import { nextFile, previousFile, updateViewCoords } from '../_actions';
import { VISIBLE_ROWS } from '../_consts';
import {
  getColumnPage,
  getColumnPages,
  getFileCount,
  getFirstColumnCoord,
  getFirstRowCoord,
  getLastColumnCoord,
  getLastRowCoord,
  getNextColumnCoord,
  getNextRowCoord,
  getPreviousColumnCoord,
  getPreviousRowCoord,
  getRowPage,
  getRowPages,
  getSelectedFile,
  getTotalColumns,
  getTotalRows,
  getVisibleColumns
} from '../_selectors';

type Coord = {
  row: number;
  column: number;
};

interface ITableNavigation {
  visibleColumns: number;
  totalColumns: number;
  totalRows: number;
  firstColumnCoord: Coord;
  previousColumnCoord: Coord;
  nextColumnCoord: Coord;
  lastColumnCoord: Coord;
  firstRowCoord: Coord;
  previousRowCoord: Coord;
  nextRowCoord: Coord;
  lastRowCoord: Coord;
  rowPages: number;
  rowPage: number;
  columnPages: number;
  columnPage: number;
  updateViewCoords: (coords: { row: number; column: number }) => void;
  previousFile: () => void;
  nextFile: () => void;
  fileCount: number;
  selectedFile: any;
  showFileName?: boolean;
}

const TableNavigation: FC<ITableNavigation> = ({
  visibleColumns,
  totalColumns,
  totalRows,
  firstColumnCoord,
  previousColumnCoord,
  nextColumnCoord,
  lastColumnCoord,
  firstRowCoord,
  previousRowCoord,
  nextRowCoord,
  lastRowCoord,
  rowPages,
  rowPage,
  columnPages,
  columnPage,
  updateViewCoords,
  previousFile,
  nextFile,
  fileCount,
  selectedFile,
  showFileName
}) => {
  const t = useTranslations();
  return (
    <>
      {showFileName && fileCount > 1 && (
        <p>
          {t('labels.file_name')}: {selectedFile?.fileName}
        </p>
      )}
      <div className="flex">
        {fileCount > 1 && (
          <div className="mr-2 mt-4 flex gap-2">
            <Button
              size="small"
              variant="icon-btn"
              color="neutral"
              className="!h-10 !w-10 rounded-full"
              onClick={() => nextFile()}
            >
              <ArrowSelectIcon
                className="transition-rotate h-5 w-5 rotate-90 scale-125"
                aria-hidden="true"
              />
            </Button>
            <Button
              size="small"
              variant="icon-btn"
              color="neutral"
              className="!h-10 !w-10 rounded-full"
              onClick={() => previousFile()}
            >
              <ArrowSelectIcon
                className="transition-rotate h-5 w-5 -rotate-90 scale-125"
                aria-hidden="true"
              />
            </Button>
          </div>
        )}
        <div className="flex w-full justify-center gap-4">
          {totalColumns > visibleColumns && (
            <div className="mt-4 flex items-center gap-2">
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="!h-10 !w-10 rounded-full"
                onClick={() => updateViewCoords(firstColumnCoord)}
                disabled={columnPage == 1}
              >
                <DoubleArrowSelectIcon
                  className="transition-rotate h-6 w-6 rotate-90"
                  aria-hidden="true"
                />
              </Button>
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="!h-10 !w-10 rounded-full"
                onClick={() => updateViewCoords(previousColumnCoord)}
                disabled={columnPage == 1}
              >
                <ArrowSelectIcon
                  className="transition-rotate h-5 w-5 rotate-90 scale-125"
                  aria-hidden="true"
                />
              </Button>
              <Typography className="flex w-10 justify-center" variant="light5">
                {columnPage}/{columnPages}
              </Typography>
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="!h-10 !w-10 rounded-full"
                onClick={() => updateViewCoords(nextColumnCoord)}
                disabled={columnPage == columnPages}
              >
                <ArrowSelectIcon
                  className="transition-rotate h-5 w-5 -rotate-90 scale-125"
                  aria-hidden="true"
                />
              </Button>
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="!h-10 !w-10 rounded-full"
                onClick={() => updateViewCoords(lastColumnCoord)}
                disabled={columnPage == columnPages}
              >
                <DoubleArrowSelectIcon
                  className="transition-rotate h-6 w-6 -rotate-90 scale-110"
                  aria-hidden="true"
                />
              </Button>
            </div>
          )}

          {totalRows > VISIBLE_ROWS && (
            <div className="mt-4 flex gap-2">
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="!h-10 !w-10 rounded-full"
                onClick={() => updateViewCoords(firstRowCoord)}
                disabled={rowPage == 1}
              >
                <DoubleArrowSelectIcon
                  className="transition-rotate h-6 w-6 rotate-180 scale-110"
                  aria-hidden="true"
                />
              </Button>
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="!h-10 !w-10 rounded-full"
                onClick={() => updateViewCoords(previousRowCoord)}
                disabled={rowPage == 1}
              >
                <ArrowSelectIcon
                  className="transition-rotate h-5 w-5 rotate-180 scale-125"
                  aria-hidden="true"
                />
              </Button>
              <Typography className="flex w-10 justify-center" variant="regular4">
                {rowPage}/{rowPages}
              </Typography>
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="!h-10 !w-10 rounded-full"
                onClick={() => updateViewCoords(nextRowCoord)}
                disabled={rowPage == rowPages}
              >
                <ArrowSelectIcon className="h-5 w-5 scale-125" aria-hidden="true" />
              </Button>
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="!h-10 !w-10 rounded-full"
                onClick={() => updateViewCoords(lastRowCoord)}
                disabled={rowPage == rowPages}
              >
                <DoubleArrowSelectIcon className="h-6 w-6 scale-110" aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default connect(
  (state) => ({
    visibleColumns: getVisibleColumns(state),
    totalColumns: getTotalColumns(state),
    totalRows: getTotalRows(state),
    firstColumnCoord: getFirstColumnCoord(state),
    previousColumnCoord: getPreviousColumnCoord(state),
    nextColumnCoord: getNextColumnCoord(state),
    lastColumnCoord: getLastColumnCoord(state),
    firstRowCoord: getFirstRowCoord(state),
    previousRowCoord: getPreviousRowCoord(state),
    nextRowCoord: getNextRowCoord(state),
    lastRowCoord: getLastRowCoord(state),
    rowPages: getRowPages(state),
    rowPage: getRowPage(state),
    columnPages: getColumnPages(state),
    columnPage: getColumnPage(state),
    fileCount: getFileCount(state),
    selectedFile: getSelectedFile(state)
  }),
  { updateViewCoords, previousFile, nextFile }
)(TableNavigation);
